import { Activity, AlertTriangle, Calendar, Loader2 } from "lucide-react";

const LoadingState = () => (
  <div className="border border-white/10 rounded-sm p-12 text-center bg-black">
    <Loader2 className="animate-spin mx-auto mb-4 text-white" size={32} />
    <p className="text-xs font-mono uppercase tracking-[0.3em] text-gray-400">
      Loading games...
    </p>
  </div>
);

const ErrorState = ({ message, onRetry }) => (
  <div className="border border-red-500/40 rounded-sm p-8 text-center bg-red-500/10 space-y-4">
    <AlertTriangle className="mx-auto text-red-200" size={36} />
    <p className="text-xs font-mono uppercase tracking-[0.3em] text-red-200">
      Error Loading Games
    </p>
    <p className="text-sm text-red-100/80">{message}</p>
    <button
      onClick={onRetry}
      className="px-6 py-3 border border-red-300/40 text-[11px] font-mono uppercase tracking-[0.3em] rounded-sm text-red-100 hover:border-red-100 transition-colors"
    >
      Try Again
    </button>
  </div>
);

const EmptyState = ({ date }) => (
  <div className="border border-white/10 rounded-sm p-12 text-center bg-black space-y-3">
    <Calendar className="mx-auto text-white/30" size={36} />
    <p className="text-xs font-mono uppercase tracking-[0.3em] text-white/60">
      No Games Found
    </p>
    <p className="text-[11px] text-white/40">
      {date}
    </p>
  </div>
);

const GamesSection = ({
  games,
  loading,
  error,
  pagination,
  onPageChange,
  renderGame,
  dateLabel,
  onRetry
}) => {
  const showInitialLoader = loading && games.length === 0;
  const showEmptyState = !loading && !error && games.length === 0;

  return (
    <section className="border border-white/10 rounded-sm p-6 bg-white/[0.02] space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-black uppercase tracking-tight text-white flex items-center gap-3">
          Games ({games.length})
        </h2>
        {pagination.totalPages > 1 && (
          <p className="text-[11px] font-mono uppercase tracking-[0.3em] text-gray-500">
            Page {pagination.currentPage} of {pagination.totalPages}
          </p>
        )}
      </div>

      {showInitialLoader && <LoadingState />}
      {error && <ErrorState message={error} onRetry={onRetry} />}
      {showEmptyState && <EmptyState date={dateLabel} />}

      {!showInitialLoader && !error && !showEmptyState && (
        <>
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {games
              .slice(
                (pagination.currentPage - 1) * pagination.perPage,
                pagination.currentPage * pagination.perPage
              )
              .map(renderGame)}
          </div>

          {pagination.totalPages > 1 && (
            <div className="flex items-center justify-center gap-3">
              <button
                onClick={() => onPageChange(pagination.currentPage - 1)}
                disabled={pagination.currentPage === 1}
                className="px-3 py-2 border border-white/10 text-white text-xs font-mono uppercase tracking-widest rounded-sm bg-transparent hover:border-white/30 disabled:opacity-40 transition-all"
              >
                Prev
              </button>
              <span className="px-4 py-2 text-xs font-mono uppercase tracking-[0.3em] text-gray-400">
                {pagination.currentPage} / {pagination.totalPages}
              </span>
              <button
                onClick={() => onPageChange(pagination.currentPage + 1)}
                disabled={pagination.currentPage === pagination.totalPages}
                className="px-3 py-2 border border-white/10 text-white text-xs font-mono uppercase tracking-widest rounded-sm bg-transparent hover:border-white/30 disabled:opacity-40 transition-all"
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </section>
  );
};

export default GamesSection;
